/**
 * «Tu trabajo de hoy»: el inicio muestra TRABAJO, no un índice de pantallas.
 *
 * Antes el inicio se presentaba diciendo cuántos botones tenía —«Contabilidad ·
 * 15 pantallas»—, que es exactamente lo que nadie compra de un ERP. Este bloque
 * lo reemplaza por lo único que le importa a quien entra a la mañana: qué hay
 * pendiente y por dónde se empieza.
 *
 * Dos partes:
 *
 *  1. **Acciones rápidas**: los verbos del día —emitir una factura, registrar un
 *     cobro— como un botón que lleva DIRECTO al formulario ya tipado (el enlace
 *     entra por el menú, así que el comprobante nace siendo «Factura de cliente»
 *     sin elegir el tipo). Emitir una factura pasa de «adivinar en qué área está»
 *     a un clic.
 *  2. **Pendientes**: contadores REALES contra la API genérica (`read_group` y el
 *     `count` de la lista), nunca hardcodeados. Cobros por cobrar, presupuestos
 *     por confirmar, tickets sin resolver, borradores. Un contador en cero, o de
 *     un módulo que no está instalado, no se muestra: si no hay trabajo de ese
 *     tipo, no ocupa lugar.
 *
 * Acá vivía un quinto contador, «Períodos por liquidar», y era el ÚNICO modelo
 * del vertical de consorcios clavado en el frontend. Se fue con la funcionalidad
 * de expensas: el comprador no liquida expensas, y un contador que apunta a una
 * pantalla que ya no existe deja el inicio ofreciendo trabajo inexistente.
 *
 * La ventana «Próximos a vencer» —lo primero que pidió el dueño— vive aparte, en
 * `ProximosPanel`, y `HomePage` la monta arriba de todo. Esto es lo que la
 * acompaña.
 */

import { useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';

import {
  IconArrowRight,
  IconContact,
  IconFileText,
  IconLifeBuoy,
  IconTrending,
  IconWallet,
} from '@/components/shell/icons';
import { formatCurrency } from '@/components/fields/format';
import { modelPath, recordPath } from '@/components/shell/menu-utils';
import { opcionesDeMetaDelModelo } from '@/components/shell/queries';
import { api, type ResultadoDeConteo } from '@/lib/api';
import type { Domain } from '@/lib/types';
import { useSessionStore } from '@/store/session';

/** Pesos sin centavos: los contadores de pendientes muestran el total redondo. */
const dinero = (valor: number): string =>
  formatCurrency(valor, { currency: 'ARS', maximumFractionDigits: 0 });

// ---------------------------------------------------------------------------
// Acciones rápidas: los verbos del día, no las pantallas.
// ---------------------------------------------------------------------------
interface AccionRapida {
  label: string;
  /** Módulo que la habilita: si no está instalado, la acción no aparece. */
  modulo: string;
  /** Modelo que se va a dar de alta. Es contra éste que se pide el permiso real. */
  model: string;
  href: string;
  icon: (props: { className?: string }) => JSX.Element;
  primaria?: boolean;
}

const ACCIONES: AccionRapida[] = [
  {
    label: 'Emitir factura',
    modulo: 'invoicing',
    model: 'invoicing.invoice',
    href: recordPath('invoicing.invoice', 'new', 'invoicing.menu_customer_invoice'),
    icon: IconFileText,
    primaria: true,
  },
  {
    label: 'Registrar cobro',
    modulo: 'invoicing',
    model: 'invoicing.payment',
    href: recordPath('invoicing.payment', 'new', 'invoicing.menu_payment_inbound'),
    icon: IconWallet,
  },
  {
    label: 'Nuevo presupuesto',
    modulo: 'sales',
    model: 'sales.order',
    href: recordPath('sales.order', 'new', 'sales.menu_quotation'),
    icon: IconTrending,
  },
  {
    label: 'Nuevo contacto',
    modulo: 'base',
    model: 'base.partner',
    href: recordPath('base.partner', 'new', 'base.menu_contacts_customers'),
    icon: IconContact,
  },
];

/**
 * Cuáles de esas acciones puede realmente ejecutar el usuario.
 *
 * Que el módulo esté instalado no quiere decir que la persona pueda dar de alta
 * en él: los permisos son por (modelo, grupo) y llegan **ya evaluados** en la
 * metadata (`permissions.create`), igual que los usa el resto del renderer. Sin
 * este filtro, un empleado de rol acotado veía los cuatro botones y cada clic lo
 * dejaba en la pantalla de «sin acceso», con el nombre técnico del modelo en las
 * migas —o sea, el ERP le ofrecía trabajo que no puede hacer y encima le
 * mostraba su tabla interna al rebotarlo—.
 *
 * Un modelo que devuelve 403 o 404 cuenta como "no puede": la metadata de un
 * modelo ilegible es exactamente la respuesta que da el backend cuando el
 * usuario no lo toca.
 */
async function cargarPermisosDeAlta(
  cliente: QueryClient,
  companyId: number | null,
  modelos: string[],
): Promise<Record<string, boolean>> {
  // LA MISMA METADATA QUE EL RESTO DE LA PANTALLA, de la caché compartida (y de
  // lo guardado en el navegador): antes eran cuatro pedidos propios de la
  // metadata ENTERA de cuatro modelos, en cada ingreso, sólo para leer un
  // booleano. Ver `opcionesDeMetaDelModelo`.
  const pares = await Promise.all(
    modelos.map(async (modelo): Promise<[string, boolean]> => {
      try {
        const spec = await cliente.ensureQueryData(opcionesDeMetaDelModelo(companyId, modelo));
        return [modelo, spec.permissions.create];
      } catch {
        return [modelo, false];
      }
    }),
  );
  return Object.fromEntries(pares);
}

// ---------------------------------------------------------------------------
// Pendientes: contadores reales contra la API.
// ---------------------------------------------------------------------------
type Tono = 'danger' | 'warning' | 'accent';

interface Pendiente {
  key: string;
  label: string;
  /** Qué falta hacer, en una línea. */
  detalle: string;
  modulo: string;
  model: string;
  domain: Domain;
  /** Menú al que lleva la tarjeta: la misma pantalla filtrada del árbol. */
  menu: string;
  tono: Tono;
  icon: (props: { className?: string }) => JSX.Element;
  /** Si está, además del conteo se suma este campo y se muestra en pesos. */
  sumField?: string;
}

const PENDIENTES: Pendiente[] = [
  {
    key: 'cobros',
    label: 'Por cobrar',
    detalle: 'Comprobantes de cliente emitidos y sin saldar',
    modulo: 'invoicing',
    model: 'invoicing.invoice',
    domain: [
      ['move_type', 'in', ['out_invoice', 'out_refund', 'out_debit']],
      ['state', '=', 'open'],
      ['amount_residual', '!=', 0],
    ],
    menu: 'invoicing.menu_customer_unpaid',
    tono: 'danger',
    icon: IconWallet,
    sumField: 'amount_residual',
  },
  {
    key: 'borradores',
    label: 'Facturas en borrador',
    detalle: 'Comprobantes empezados y sin emitir',
    modulo: 'invoicing',
    model: 'invoicing.invoice',
    domain: [
      ['move_type', '=', 'out_invoice'],
      ['state', '=', 'draft'],
    ],
    menu: 'invoicing.menu_invoice_draft',
    tono: 'warning',
    icon: IconFileText,
  },
  {
    key: 'presupuestos',
    label: 'Presupuestos por confirmar',
    detalle: 'Enviados que todavía no se volvieron pedido',
    modulo: 'sales',
    model: 'sales.order',
    domain: [['state', 'in', ['draft', 'sent']]],
    menu: 'sales.menu_quotation',
    tono: 'accent',
    icon: IconTrending,
  },
  {
    key: 'tickets',
    label: 'Tickets sin resolver',
    detalle: 'Reclamos abiertos en la mesa de ayuda',
    modulo: 'services',
    model: 'services.ticket',
    domain: [['closed_at', '=', null]],
    menu: 'services.menu_tickets_open',
    tono: 'accent',
    icon: IconLifeBuoy,
  },
  {
    key: 'asientos',
    label: 'Asientos por asentar',
    detalle: 'Registraciones en borrador',
    modulo: 'accounting',
    model: 'accounting.journal_entry',
    domain: [['state', '=', 'draft']],
    menu: 'accounting.menu_entries_draft',
    tono: 'accent',
    icon: IconWallet,
  },
];

interface Conteo {
  p: Pendiente;
  count: number;
  sum: number | null;
}

const TONO_CLASSES: Record<Tono, string> = {
  danger: 'bg-danger-soft text-danger',
  warning: 'bg-warning-soft text-warning',
  accent: 'bg-accent-soft text-accent',
};

/**
 * Trae cada contador en paralelo. Cada pendiente es independiente: si uno falla
 * (un modelo sin permiso, por ejemplo) se lo cuenta como cero y desaparece, en
 * vez de tumbar todo el bloque.
 */
async function cargarPendientes(instalados: Set<string>, signal: AbortSignal): Promise<Conteo[]> {
  const visibles = PENDIENTES.filter((p) => instalados.has(p.modulo));
  if (visibles.length === 0) return [];
  // TODOS LOS CONTEOS EN UN PEDIDO (`/contar/`), no una lista de una fila por
  // cada uno: cada pedido suelto pagaba su costo fijo y el servidor atiende de
  // a seis. Un modelo sin permiso vuelve con su error y cuenta como cero, igual
  // que antes cuando su pedido fallaba.
  let conteos: ResultadoDeConteo[];
  try {
    conteos = await api.contar(
      visibles.map((p) => ({ model: p.model, domain: p.domain })),
      signal,
    );
  } catch {
    conteos = visibles.map(() => ({ count: 0 }));
  }
  const resultados = await Promise.all(
    visibles.map(async (p, indice): Promise<Conteo> => {
      try {
        const lista = { count: conteos[indice]?.count ?? 0 };
        let sum: number | null = null;
        if (p.sumField !== undefined && lista.count > 0) {
          const grupos = await api.readGroup(
            p.model,
            { groupby: ['state'], aggregates: [`${p.sumField}:sum`], domain: p.domain },
            signal,
          );
          sum = grupos.groups.reduce((total, g) => {
            const bruto = g[`${p.sumField}:sum`];
            return total + (typeof bruto === 'string' ? Number.parseFloat(bruto) : Number(bruto ?? 0));
          }, 0);
        }
        return { p, count: lista.count, sum };
      } catch {
        return { p, count: 0, sum: null };
      }
    }),
  );
  return resultados.filter((r) => r.count > 0);
}

export function TrabajoHoy() {
  const cliente = useQueryClient();
  const modules = useSessionStore((estado) => estado.modules);
  const companyId = useSessionStore((estado) => estado.activeCompanyId);
  const autenticado = useSessionStore((estado) => estado.status === 'authenticated');
  const instalados = new Set(modules);

  // Primer tamiz, barato: lo que ni siquiera está instalado.
  const candidatas = ACCIONES.filter((a) => instalados.has(a.modulo));

  const { data: permisos } = useQuery({
    queryKey: ['acciones-rapidas', companyId, candidatas.map((a) => a.model).join(',')],
    queryFn: () => cargarPermisosDeAlta(cliente, companyId, candidatas.map((a) => a.model)),
    enabled: autenticado && candidatas.length > 0,
    staleTime: 10 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  // Segundo tamiz, el que importa: el permiso real de creación. Mientras la
  // respuesta no llegó no se dibuja ninguno —mostrarlos y sacarlos después es
  // ofrecer y retirar—, y si no queda ninguno la fila entera desaparece.
  const acciones = permisos === undefined ? [] : candidatas.filter((a) => permisos[a.model] === true);

  const { data, isPending } = useQuery({
    queryKey: ['trabajo-hoy', companyId, modules.join(',')],
    queryFn: ({ signal }) => cargarPendientes(instalados, signal),
    enabled: autenticado,
    staleTime: 60 * 1000,
    refetchOnWindowFocus: false,
  });

  const pendientes = data ?? [];

  // Sin acciones que pueda ejecutar y sin nada pendiente, el bloque entero no
  // existe: un `<section>` vacío dejaba además un `aria-labelledby` apuntando a
  // un encabezado que no se dibuja.
  if (acciones.length === 0 && (isPending || pendientes.length === 0)) return null;

  return (
    <section aria-labelledby="trabajo-titulo" className="mt-8">
      {acciones.length > 0 && (
        <div className="mb-5 flex flex-wrap gap-2">
          {acciones.map((a) => {
            const Icono = a.icon;
            return (
              <Link
                key={a.label}
                to={a.href}
                className={
                  a.primaria === true
                    ? 'inline-flex items-center gap-2 rounded-md bg-accent px-4 py-2.5 text-sm font-semibold text-accent-contrast shadow-sm transition-colors hover:bg-accent-hover'
                    : 'inline-flex items-center gap-2 rounded-md border border-line bg-surface px-4 py-2.5 text-sm font-medium text-fg transition-colors hover:border-line-strong hover:bg-surface-raised'
                }
              >
                <Icono className="h-4 w-4" />
                {a.label}
              </Link>
            );
          })}
        </div>
      )}

      {/* El bloque de pendientes sólo aparece si hay algo pendiente: un inicio sin
          trabajo no tiene por qué inventar tarjetas para llenarse. */}
      {!isPending && pendientes.length > 0 && (
        <>
          <h2
            id="trabajo-titulo"
            className="mb-3 text-sm font-semibold uppercase tracking-wide text-fg-faint"
          >
            Pendientes
          </h2>
          <ul className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
            {pendientes.map(({ p, count, sum }) => {
              const Icono = p.icon;
              return (
                <li key={p.key}>
                  <Link
                    to={modelPath(p.model, p.menu)}
                    className="group flex h-full items-start gap-3 rounded-xl border border-line bg-surface p-4 shadow-sm transition-colors hover:border-accent/40 hover:bg-surface-raised"
                  >
                    <span
                      className={`grid h-10 w-10 shrink-0 place-items-center rounded-lg ${TONO_CLASSES[p.tono]}`}
                    >
                      <Icono className="h-5 w-5" />
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="flex items-baseline gap-2">
                        <span className="text-2xl font-semibold tabular-nums leading-none">{count}</span>
                        {sum !== null && (
                          <span className="truncate text-sm font-medium text-fg-muted">
                            {dinero(sum)}
                          </span>
                        )}
                      </span>
                      <span className="mt-1 block truncate font-medium">{p.label}</span>
                      <span className="block truncate text-xs text-fg-faint">{p.detalle}</span>
                    </span>
                    <IconArrowRight className="mt-1 h-4 w-4 shrink-0 text-fg-faint opacity-0 transition-opacity group-hover:opacity-100" />
                  </Link>
                </li>
              );
            })}
          </ul>
        </>
      )}
    </section>
  );
}
